import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { getIpirScanStatus } from 'redux/features/emailServer/emailServerSlice'
import { isFailed, isSuccess } from 'redux/toolkit/api'
import routesConfig from 'lib/routesConfig'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import { getAvatarInitials } from 'lib/avatarInitials'
import { IPIRAppTypes } from '../../types/egdWizardTypes'

export interface State {
  connected: boolean
  userEmail?: string
  userName?: string
  accountName?: string
  initials?: string
  ipirScanStatus: boolean
  skipDialogOpen: boolean
}

export interface EventHandlers {
  onConnect: () => void
  onSkip: () => void
  onClose: () => void
}

export type MicrosoftConnectedLogic = [State, EventHandlers]

export const useMicrosoftConnectedLogic = (): MicrosoftConnectedLogic => {
  const dispatch = useAppDispatch()
  const [skipDialogOpen, setSkipDialogOpen] = useState(false)

  const {
    userName,
    userEmail,
    accountName,
    ipirAccountId,
    ipirTokenId,
    ipirScanStatus,
    isIpirScanStatusSuccess,
    isIpirScanStatusFailed
  } = useAppSelector(_store => ({
    userName: _store.auth.accessTokenObject?.userInfo?.name,
    userEmail: _store.auth.accessTokenObject?.userId,
    accountName: _store.auth.accessTokenObject?.userInfo?.accounts?.find(
      account => account.id === _store.auth.accessTokenObject?.bccAccountId
    )?.name,
    ipirAccountId: _store.emailServer.emailServerSettings?.ipirAccountId,
    ipirTokenId: _store.emailServer.emailServerSettings?.ipirTokenId,
    ipirScanStatus: _store.emailServer.ipirScanStatus,
    isIpirScanStatusSuccess: isSuccess(_store.emailServer.getIpirScanStatusApiStatus),
    isIpirScanStatusFailed: isFailed(_store.emailServer.getIpirScanStatusApiStatus)
  }))

  useEffect(() => {
    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_1_START)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!isIpirScanStatusSuccess && !isIpirScanStatusFailed && ipirAccountId && ipirTokenId) {
      dispatch(getIpirScanStatus())
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_1_SCAN_STATUS)
    }
  }, [dispatch, isIpirScanStatusSuccess, isIpirScanStatusFailed, ipirAccountId, ipirTokenId])

  const initials = useMemo(() => getAvatarInitials({ accountName, email: userEmail }), [accountName, userEmail])

  const onConnect = useCallback(() => {
    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_1_CONNECT)
    routesConfig.SETUP_FLOW_IPIR_AUTHORIZE.goto({}, false, `?appType=${IPIRAppTypes.Sentinel}`)
  }, [])

  const onSkip = useCallback(() => {
    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_1_SKIP)
    setSkipDialogOpen(true)
  }, [])

  const onClose = useCallback(() => {
    trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_1_SKIP_CANCEL)
    setSkipDialogOpen(false)
  }, [])

  return useMemo(
    () => [
      {
        connected: !!ipirAccountId && !!ipirTokenId,
        userEmail,
        userName,
        accountName,
        initials,
        ipirScanStatus,
        skipDialogOpen
      },
      {
        onConnect,
        onSkip,
        onClose
      }
    ],
    [
      userEmail,
      userName,
      accountName,
      initials,
      ipirScanStatus,
      ipirAccountId,
      ipirTokenId,
      skipDialogOpen,
      onConnect,
      onSkip,
      onClose
    ]
  )
}
